const basicStringFieldTypes = [
    'short_text',
    'paragraph_text',
    'rich_text',
];
const compoundStringFieldTypes = [
    'name',
    'email',
    'address',
    'phone',
    'link',
    'date_time',
];
const multipleChoiceField = [
    'multiple_choice',
];
const basicNumericFieldTypes = [
    'number',
    'currency',
];
const limitedNumericFieldTypes = [
    'rating',
    'boolean',
];
const formulaFieldTypes = [
    // formula fields
    'sum',
    'min',
    'max',
    'average',
    'count',
    // equations
    'concatenation',
    'equation',
];
const restrictedFieldTypes = [
    // these can never be converted to
    'auto_increment',
    'file',
    'image',
    'signature',
    // currently you can't convert to these meaningfully
    'connection',
    'timer',
];
// eslint-disable-next-line import/prefer-default-export
export const fieldTypes = {
    text: [
        {
            name: 'Short Text',
            type: 'short_text',
            description: 'Short Text is used for text that is less than 255 characters and doesn\'t require any special formatting.',
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Paragraph Text',
            type: 'paragraph_text',
            description: 'Paragraph Text is used for text that is longer and might need multiple paragraphs.',
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Rich Text',
            type: 'rich_text',
            description: 'Rich Text is used when you need to add formatting to text.',
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Text Formula',
            type: 'concatenation',
            description: '',
            format: {
                count_field: 'Connection',
                equation: '',
                equation_type: 'numeric',
                formula_field: 'Field',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
    ],
    number: [
        {
            name: 'Number',
            type: 'number',
            description: 'A Number is used for fields that are added, counted, or measured.',
            format: {
                mark_decimal: 'none',
                precision: 2,
                rounding: 'none',
                mark_thousands: 'none',
                format: '',
            },
            conversions: {
                always: [
                    ...basicNumericFieldTypes,
                    ...basicStringFieldTypes,
                ],
                sometimes: [],
            },
        },
        {
            name: 'Currency',
            type: 'currency',
            description: 'Currency automatically adds the $ symbol and formats for decimals and commas.',
            format: {
                format: '$',
            },
            conversions: {
                always: [
                    ...basicNumericFieldTypes,
                    ...basicStringFieldTypes,
                ],
                sometimes: [],
            },
        },
        {
            name: 'Equation',
            type: 'equation',
            description: 'Use fields to enter simple equations like (Sales - Expenses) * 12',
            format: {
                count_field: 'Connection',
                equation: '',
                equation_type: 'numeric',
                date_type: 'days',
                date_result: 'number',
                date_format: 'mm/dd/yyyy',
                time_format: 'Ignore Time',
                formula_field: 'Field',
                mark_decimal: 'none',
                mark_thousands: 'none',
                post: '',
                pre: '',
                precision: '2',
                rounding: 'none',
                format: '',
            },
            conversions: {
                always: [...basicStringFieldTypes, 'number'],
                sometimes: [],
            },
        },
        {
            name: 'Auto Increment',
            type: 'auto_increment',
            description: 'Auto Increment is used to give each entry a unique ID by starting at zero and adding one for each new record.',
            conversions: {
                always: [
                    ...basicNumericFieldTypes,
                    ...basicStringFieldTypes,
                ],
                sometimes: [],
            },
        },
        {
            name: 'Sum',
            type: 'sum',
            description: 'Store the total value of connected record fields.',
            format: {
                connection: {
                    key: '',
                },
                field: {
                    key: '',
                },
                mark_decimal: 'none',
                mark_thousands: 'none',
                post: '',
                pre: '',
                precision: '2',
                rounding: 'none',
                format: '',
            },
            isFormula: true,
            conversions: {
                always: [...basicStringFieldTypes, 'number'],
                sometimes: [],
            },
        },
        {
            name: 'Min',
            type: 'min',
            description: 'Store the minimum value of connected record fields.',
            format: {
                connection: {
                    key: '',
                },
                field: {
                    key: '',
                },
                mark_decimal: 'none',
                mark_thousands: 'none',
                post: '',
                pre: '',
                precision: '2',
                rounding: 'none',
                format: '',
            },
            isFormula: true,
            conversions: {
                always: [...basicStringFieldTypes, 'number'],
                sometimes: [],
            },
        },
        {
            name: 'Max',
            type: 'max',
            description: 'Store the maximum value of connected record fields.',
            format: {
                connection: {
                    key: '',
                },
                field: {
                    key: '',
                },
                mark_decimal: 'none',
                mark_thousands: 'none',
                post: '',
                pre: '',
                precision: '2',
                rounding: 'none',
                format: '',
            },
            isFormula: true,
            conversions: {
                always: [...basicStringFieldTypes, 'number'],
                sometimes: [],
            },
        },
        {
            name: 'Average',
            type: 'average',
            description: 'Store the average value of connected record fields.',
            format: {
                connection: {
                    key: '',
                },
                field: {
                    key: '',
                },
                mark_decimal: 'none',
                mark_thousands: 'none',
                post: '',
                pre: '',
                precision: '2',
                rounding: 'none',
                format: '',
            },
            isFormula: true,
            conversions: {
                always: [...basicStringFieldTypes, 'number'],
                sometimes: [],
            },
        },
        {
            name: 'Count',
            type: 'count',
            description: 'Store the average value of connected record fields.',
            format: {
                connection: '',
                mark_decimal: 'none',
                mark_thousands: 'none',
                post: '',
                pre: '',
                precision: '2',
                rounding: 'none',
                format: '',
            },
            isFormula: true,
            conversions: {
                always: [...basicStringFieldTypes, 'number'],
                sometimes: [],
            },
        },
    ],
    choice: [
        {
            name: 'Multiple Choice',
            type: 'multiple_choice',
            description: 'Multiple Choice is used to select a choice from a group of options.',
            format: {
                blank: 'Select...',
                default: 'kn-blank',
                options: [
                    'First Choice',
                    'Second Choice',
                    'Third Choice',
                ],
                sorting: 'alphabetical',
                type: 'single',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Yes/No',
            type: 'boolean',
            description: 'Yes/No is used for fields that are either "Yes" or "No".  They can also be On/Off or True/False.',
            format: {
                default: false,
                format: 'yes_no',
                input: 'dropdown',
                required: false,
            },
            conversions: {
                always: [
                    ...basicStringFieldTypes,
                    ...basicNumericFieldTypes,
                    ...limitedNumericFieldTypes,
                ],
                sometimes: [],
            },
        },
    ],
    date: [
        {
            name: 'Date/Time',
            type: 'date_time',
            description: 'Date/Time is used for dates, times, or both.',
            format: {
                date_format: 'mm/dd/yyyy',
                default_type: 'current',
                time_format: 'Ignore Time',
                default_time: 'none',
                calendar: false,
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Timer',
            type: 'timer',
            description: 'A Timer is used to track a time duration.  Great for tracking hours.',
            format: {
                date_format: 'mm/dd/yyyy',
                minutes: '1',
                time_format: 'HH:MM am',
                total_format: 'from-to',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
    ],
    file: [
        {
            name: 'File',
            type: 'file',
            description: 'Files are used to add a downloadable file to a record.',
            format: {
                secure: false,
            },
            conversions: {
                always: [],
                sometimes: [],
            },
        },
        {
            name: 'Image',
            type: 'image',
            description: 'Images are used to display an image with an record.  You can also create thumbnail versions of the image to display where needed.',
            format: {
                source: 'upload',
                thumbs: [],
            },
            conversions: {
                always: [],
                sometimes: [],
            },
        },
    ],
    personal: [
        {
            name: 'Person',
            type: 'name',
            description: 'A Person combines fields like Title, First Name, and Last Name into one convenient field for entering and displaying persons.',
            format: {
                format: 'First Last',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Email',
            type: 'email',
            description: 'An Email is used for email addresses.  A link will automatically be created to send an email.',
            format: {
                text_format: 'url',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Address',
            type: 'address',
            description: 'An Address combines fields like Street and City into one convenient field for entering and displaying address data.',
            format: {
                enable_autocomplete: false,
                format: 'US',
                input: 'address',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Phone',
            type: 'phone',
            description: 'An Phone field is used to mask numbers in phone formats.  A link will automatically be created to call the number in mobile devices.',
            format: {
                extension: false,
                format: '(999) 999-9999',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
    ],
    other: [
        {
            name: 'Link',
            type: 'link',
            description: 'A Link is used when you need to link to another website.',
            format: {
                target: '_self',
                text_format: 'url',
            },
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
        {
            name: 'Signature',
            type: 'signature',
            description: 'Allow users add their signature with their finger or mouse.',
            conversions: {
                always: [],
                sometimes: [],
            },
        },
        {
            name: 'Rating',
            type: 'rating',
            description: 'Allow users to rate records with a 5-star system.',
            format: {
                allow_half: false,
                max: '3',
            },
            conversions: {
                always: [
                    ...basicNumericFieldTypes,
                    ...basicStringFieldTypes,
                ],
                sometimes: [],
            },
        },
        {
            name: 'Connection',
            type: 'connection',
            description: 'Allow records to connect to records from other objects.',
            conversions: {
                always: basicStringFieldTypes,
                sometimes: [],
            },
        },
    ],
};
